import { ElementType, CharacterStats } from '@shared/types';
import { COMBAT } from '@shared/constants';
import { StatCalculator, StatType } from '@shared/modifiers';

export interface AilmentApplication {
  type: 'bleed' | 'burn' | 'poison' | 'chill' | 'shock';
  chance: number;       // Effective chance after resistance
  damage: number;       // Base ailment damage (for DoTs)
  applied: boolean;     // Whether it was actually applied (roll succeeded)
}

export interface CombatResult {
  damage: number;
  isCritical: boolean;
  isDodged: boolean;
  isBlocked: boolean;
  damageType: ElementType;
  ailment?: AilmentApplication; // Ailment info if applicable
  bypassES?: boolean;          // true for karanlik damage
}

export interface CombatContext {
  attackerCalculator?: StatCalculator;
  defenderCalculator?: StatCalculator;
  isSpell?: boolean;  // true = skill attack, applies spellDamage
}

/**
 * Skill damage preview for UI - shows expected damage values
 * This uses the same formulas as actual combat
 */
export interface SkillDamagePreview {
  baseDamage: number;
  skillMultiplier: number;
  damageModifier: number;
  elementModifier: number;
  finalDamage: number;
  critChance: number;
  critMultiplier: number;
  critDamage: number;
  avgDamage: number;
  dps: number;
  effectiveCooldown: number;
  attackSpeed: number;
  cooldownReduction: number;
  lifeLeech: number;
  manaLeech: number;
  areaMultiplier: number;
}

export class CombatSystem {
  /**
   * Calculate damage after applying all modifiers
   */
  static calculateDamage(
    baseDamage: number,
    _attackerStats: Partial<CharacterStats>,
    defenderStats: CharacterStats,
    damageType: ElementType = 'physical',
    attackerLevel: number = 1
  ): CombatResult {
    // Check for dodge
    const dodgeChance = defenderStats.evasion / (defenderStats.evasion + 100);
    if (Math.random() < dodgeChance) {
      return {
        damage: 0,
        isCritical: false,
        isDodged: true,
        isBlocked: false,
        damageType,
      };
    }

    // Check for block
    const blockChance = defenderStats.blockChance || 0;
    if (blockChance > 0 && Math.random() * 100 < Math.min(blockChance, 75)) {
      return { damage: 0, isCritical: false, isDodged: false, isBlocked: true, damageType };
    }

    let damage = baseDamage;

    // Apply critical hit
    const critChance = COMBAT.BASE_CRIT_CHANCE;
    const isCritical = Math.random() < critChance;
    if (isCritical) {
      damage *= COMBAT.BASE_CRIT_MULTIPLIER;
    }

    // Apply damage reduction based on type
    if (damageType === 'physical') {
      damage = this.applyArmorReduction(damage, defenderStats.armor, attackerLevel);
    } else {
      // Elemental damage - apply resistance
      damage = this.applyElementalResistance(damage, damageType, defenderStats);
    }

    // Round damage
    damage = Math.floor(damage);

    return {
      damage: Math.max(1, damage), // Minimum 1 damage
      isCritical,
      isDodged: false,
      isBlocked: false,
      damageType,
    };
  }

  /**
   * Apply armor reduction to physical damage
   * Formula: reduction = armor / (armor + 10 * enemyLevel)
   */
  static applyArmorReduction(
    damage: number,
    armor: number,
    attackerLevel: number
  ): number {
    if (armor <= 0) return damage;

    const reduction = armor / (armor + COMBAT.ARMOR_CONSTANT * attackerLevel);
    const cappedReduction = Math.min(reduction, COMBAT.MAX_ARMOR_REDUCTION);

    return damage * (1 - cappedReduction);
  }

  /**
   * Apply elemental resistance
   */
  static applyElementalResistance(
    damage: number,
    element: ElementType,
    _defenderStats: CharacterStats,
    defenderCalculator?: StatCalculator
  ): number {
    // Use StatCalculator if available for full resistance calculation
    if (defenderCalculator) {
      const resistStat = this.getResistStatForElement(element);
      if (resistStat) {
        let resistance = defenderCalculator.calculate(resistStat, 0);
        // Add allResist to element-specific resistance
        if (resistStat !== 'physicalResist') {
          resistance += defenderCalculator.calculate('allResist', 0);
        }
        const cappedResistance = Math.min(resistance, COMBAT.MAX_ELEMENTAL_RESISTANCE * 100);
        return damage * (1 - cappedResistance / 100);
      }
    }
    return damage;
  }

  /**
   * Get the resistance stat for an element
   */
  private static getResistStatForElement(element: ElementType): StatType | null {
    const map: Partial<Record<ElementType, StatType>> = {
      'od': 'odResist',
      'su': 'suResist',
      'gok': 'gokResist',
      'karanlik': 'karanlikResist',
      'physical': 'physicalResist',
    };
    return map[element] || null;
  }

  /**
   * Calculate damage using StatCalculator for full modifier support
   * This is the new recommended method for damage calculation
   */
  static calculateDamageWithModifiers(
    baseDamage: number,
    damageType: ElementType,
    context: CombatContext,
    attackerLevel: number = 1
  ): CombatResult {
    const { attackerCalculator, defenderCalculator } = context;

    // Get calculated stats from attacker if available
    let effectiveDamage = baseDamage;
    let critChance = COMBAT.BASE_CRIT_CHANCE * 100; // Convert to percentage
    let critMultiplier = COMBAT.BASE_CRIT_MULTIPLIER * 100; // Convert to percentage

    if (attackerCalculator) {
      // Apply damage modifiers
      const damageMod = attackerCalculator.calculate('damage', 100) / 100;
      effectiveDamage *= damageMod;

      // Apply element-specific damage
      const elementDamageStat = this.getDamageStatForElement(damageType);
      if (elementDamageStat) {
        const elementMod = attackerCalculator.calculate(elementDamageStat, 100) / 100;
        effectiveDamage *= elementMod;
      }

      // Apply generic elemental damage bonus for non-physical damage types
      if (damageType !== 'physical') {
        const elemDmgMod = attackerCalculator.calculate('elementalDamage', 100) / 100;
        effectiveDamage *= elemDmgMod;
      }

      // Apply spell damage modifier for skill attacks
      if (context.isSpell) {
        const spellMod = attackerCalculator.calculate('spellDamage', 100) / 100;
        effectiveDamage *= spellMod;
      }

      // Get crit stats
      critChance = attackerCalculator.calculate('critChance', COMBAT.BASE_CRIT_CHANCE * 100);
      critMultiplier = attackerCalculator.calculate('critDamage', COMBAT.BASE_CRIT_MULTIPLIER * 100);
    }

    // Get defender stats
    let evasion = 0;
    let armor = 0;

    if (defenderCalculator) {
      evasion = defenderCalculator.calculate('evasion', 0);
      armor = defenderCalculator.calculate('armor', 0);
    }

    // Check for dodge
    const dodgeChance = evasion / (evasion + 100);
    if (Math.random() < dodgeChance) {
      return {
        damage: 0,
        isCritical: false,
        isDodged: true,
        isBlocked: false,
        damageType,
      };
    }

    // Check for block (defender side)
    if (defenderCalculator) {
      const blockChance = defenderCalculator.calculate('blockChance', 0);
      if (blockChance > 0 && Math.random() * 100 < Math.min(blockChance, 75)) {
        return { damage: 0, isCritical: false, isDodged: false, isBlocked: true, damageType };
      }
    }

    // Apply critical hit
    const isCritical = Math.random() * 100 < critChance;
    if (isCritical) {
      effectiveDamage *= critMultiplier / 100;
    }

    // Apply damage reduction based on type
    if (damageType === 'physical') {
      effectiveDamage = this.applyArmorReduction(effectiveDamage, armor, attackerLevel);
    } else {
      effectiveDamage = this.applyElementalResistance(effectiveDamage, damageType, {} as CharacterStats, defenderCalculator);
    }

    // Round damage
    effectiveDamage = Math.floor(effectiveDamage);

    // Calculate ailment
    const ailment = this.calculateAilment(
      effectiveDamage,
      damageType,
      isCritical,
      attackerCalculator,
      defenderCalculator,
    );

    return {
      damage: Math.max(1, effectiveDamage),
      isCritical,
      isDodged: false,
      isBlocked: false,
      damageType,
      ailment,
      bypassES: damageType === 'karanlik', // Chaos damage bypasses Energy Shield
    };
  }

  /**
   * Get the damage stat for an element
   */
  private static getDamageStatForElement(element: ElementType): StatType | null {
    const map: Partial<Record<ElementType, StatType>> = {
      'od': 'odDamage',
      'su': 'suDamage',
      'gok': 'gokDamage',
      'karanlik': 'karanlikDamage',
      'physical': 'physicalDamage',
    };
    return map[element] || null;
  }

  /**
   * Calculate attack speed multiplier from stats
   */
  static getAttackSpeedMultiplier(calculator: StatCalculator): number {
    const attackSpeed = calculator.calculate('attackSpeed', 100);
    return Math.max(COMBAT.MIN_ATTACK_SPEED_MULT, attackSpeed / 100);
  }

  /**
   * Calculate cast speed multiplier from stats
   */
  static getCastSpeedMultiplier(calculator: StatCalculator): number {
    const castSpeed = calculator.calculate('castSpeed', 100);
    return Math.max(COMBAT.MIN_CAST_SPEED_MULT, castSpeed / 100);
  }

  /**
   * Calculate movement speed multiplier from stats
   */
  static getMoveSpeedMultiplier(calculator: StatCalculator): number {
    const moveSpeed = calculator.calculate('moveSpeed', 100);
    return Math.max(COMBAT.MIN_MOVE_SPEED_MULT, moveSpeed / 100);
  }

  /**
   * Calculate cooldown reduction from stats
   */
  static getCooldownMultiplier(calculator: StatCalculator): number {
    const cdr = calculator.calculate('cooldownReduction', 0);
    // CDR is capped at 75%
    const cappedCdr = Math.min(cdr, 75);
    return 1 - (cappedCdr / 100);
  }

  /**
   * Calculate area of effect multiplier from stats
   */
  static getAreaMultiplier(calculator: StatCalculator): number {
    const aoe = calculator.calculate('areaOfEffect', 100);
    return aoe / 100;
  }

  /**
   * Calculate life leech from damage dealt
   */
  static calculateLeech(
    damageDealt: number,
    calculator: StatCalculator,
    leechType: 'life' | 'mana'
  ): number {
    const stat = leechType === 'life' ? 'lifeLeech' : 'manaLeech';
    const leechPercent = calculator.calculate(stat, 0);
    return Math.floor(damageDealt * leechPercent / 100);
  }

  /**
   * Calculate skill damage preview for UI
   * Uses the SAME formulas as calculateDamageWithModifiers
   */
  static calculateSkillPreview(
    baseDamage: number,
    skillMultiplier: number,
    element: ElementType,
    skillCooldown: number,
    calculator: StatCalculator
  ): SkillDamagePreview {
    // Damage modifiers - SAME as calculateDamageWithModifiers
    const damageModifier = calculator.calculate('damage', 100) / 100;

    // Element-specific damage - SAME as calculateDamageWithModifiers
    const elementDamageStat = this.getDamageStatForElement(element);
    const elementModifier = elementDamageStat
      ? calculator.calculate(elementDamageStat, 100) / 100
      : 1;

    // Generic elemental damage bonus for non-physical - SAME as calculateDamageWithModifiers
    const elemDmgModifier = element !== 'physical'
      ? calculator.calculate('elementalDamage', 100) / 100
      : 1;

    // Spell damage modifier (skills are always spells in preview)
    const spellModifier = calculator.calculate('spellDamage', 100) / 100;

    // Final damage calculation - SAME formula
    const finalDamage = Math.floor(baseDamage * skillMultiplier * damageModifier * elementModifier * elemDmgModifier * spellModifier);

    // Crit stats - SAME as calculateDamageWithModifiers
    const critChance = calculator.calculate('critChance', COMBAT.BASE_CRIT_CHANCE * 100);
    const critMultiplier = calculator.calculate('critDamage', COMBAT.BASE_CRIT_MULTIPLIER * 100);
    const critDamage = Math.floor(finalDamage * critMultiplier / 100);

    // Speed stats
    const attackSpeed = calculator.calculate('attackSpeed', 100);
    const cooldownReduction = Math.min(calculator.calculate('cooldownReduction', 0), 75);
    const effectiveCooldown = skillCooldown * (1 - cooldownReduction / 100);

    // Average damage (accounting for crits)
    const critRate = critChance / 100;
    const avgDamage = finalDamage * (1 - critRate) + critDamage * critRate;

    // DPS calculation
    const hitsPerSecond = 1 / Math.max(effectiveCooldown, 0.1);
    const dps = Math.floor(avgDamage * hitsPerSecond);

    // Leech
    const lifeLeech = calculator.calculate('lifeLeech', 0);
    const manaLeech = calculator.calculate('manaLeech', 0);

    // Area
    const areaMultiplier = calculator.calculate('areaOfEffect', 100) / 100;

    return {
      baseDamage,
      skillMultiplier,
      damageModifier,
      elementModifier,
      finalDamage,
      critChance,
      critMultiplier,
      critDamage,
      avgDamage: Math.floor(avgDamage),
      dps,
      effectiveCooldown,
      attackSpeed,
      cooldownReduction,
      lifeLeech,
      manaLeech,
      areaMultiplier,
    };
  }

  /**
   * Calculate if an attack hits based on accuracy vs evasion
   */
  static rollHit(accuracy: number, evasion: number): boolean {
    const hitChance = accuracy / (accuracy + evasion);
    return Math.random() < hitChance;
  }

  /**
   * Calculate XP reward based on enemy level and player level
   */
  static calculateExpReward(
    baseExp: number,
    enemyLevel: number,
    playerLevel: number
  ): number {
    // Reduced XP if player is much higher level
    const levelDiff = playerLevel - enemyLevel;

    if (levelDiff > 10) {
      return Math.floor(baseExp * 0.1);
    } else if (levelDiff > 5) {
      return Math.floor(baseExp * 0.5);
    } else if (levelDiff < -5) {
      // Bonus XP for higher level enemies
      return Math.floor(baseExp * 1.5);
    }

    return baseExp;
  }

  // ========================
  // Ailment System
  // ========================

  /** Map element type to its associated ailment */
  private static getAilmentForElement(element: ElementType): 'bleed' | 'burn' | 'poison' | 'chill' | 'shock' | null {
    switch (element) {
      case 'physical': return 'bleed';
      case 'od': return 'burn';
      case 'su': return 'chill';
      case 'gok': return 'shock';
      case 'karanlik': return 'poison';
      default: return null;
    }
  }

  /** Get specific ailment chance stat name for an element */
  private static getAilmentChanceStat(element: ElementType): StatType | null {
    switch (element) {
      case 'physical': return 'bleedChance';
      case 'od': return 'burnChance';
      case 'karanlik': return 'poisonChance';
      default: return null; // chill/shock don't have specific chance stats
    }
  }

  /**
   * Calculate ailment application for a hit.
   *
   * Rules:
   * - Ailment chance = (general ailmentChance + specific chance) × (1 - elementResist/100)
   * - Crit = guaranteed ailment (bypasses resistance)
   * - Ailment damage = MAX(hitDamage × ratio, baseDamage × (1 + ailmentEffect%))
   */
  static calculateAilment(
    hitDamage: number,
    damageType: ElementType,
    isCritical: boolean,
    attackerCalculator?: StatCalculator,
    defenderCalculator?: StatCalculator,
  ): AilmentApplication | undefined {
    const ailmentType = this.getAilmentForElement(damageType);
    if (!ailmentType) return undefined;

    // Calculate ailment chance
    let totalChance = 0;
    if (attackerCalculator) {
      // General ailment chance
      totalChance += attackerCalculator.calculate('ailmentChance', 0);
      // Specific ailment chance (bleedChance, burnChance, poisonChance)
      const specificStat = this.getAilmentChanceStat(damageType);
      if (specificStat) {
        totalChance += attackerCalculator.calculate(specificStat, 0);
      }
    }

    // If no chance at all and not crit, skip
    if (totalChance <= 0 && !isCritical) return undefined;

    // Apply resistance to reduce chance (crit bypasses)
    let effectiveChance = totalChance;
    if (!isCritical && defenderCalculator) {
      const resistStat = this.getResistStatForElement(damageType);
      const resistance = resistStat ? defenderCalculator.calculate(resistStat, 0) : 0;
      const allResist = damageType !== 'physical' ? defenderCalculator.calculate('allResist', 0) : 0;
      const totalResist = Math.min(resistance + allResist, damageType === 'karanlik' ? 50 : 75);
      effectiveChance = totalChance * (1 - totalResist / 100);
    }

    // Crit = guaranteed ailment
    if (isCritical) {
      effectiveChance = 100;
    }

    // Roll for application
    const applied = Math.random() * 100 < effectiveChance;

    // Calculate ailment damage (for DoT ailments: bleed, burn, poison)
    let ailmentDamage = 0;
    if (ailmentType === 'bleed' || ailmentType === 'burn' || ailmentType === 'poison') {
      // Ratio based on ailment type
      const ratios: Record<string, number> = { bleed: 0.09, burn: 0.13, poison: 0.08 };
      const hitBasedDamage = hitDamage * (ratios[ailmentType] || 0.1);

      // Independent scaling from ailmentEffect stat
      const baseTaban: Record<string, number> = { bleed: 30, burn: 50, poison: 30 };
      let effectBasedDamage = baseTaban[ailmentType] || 30;
      if (attackerCalculator) {
        const ailmentEffect = attackerCalculator.calculate('ailmentEffect', 0);
        effectBasedDamage *= (1 + ailmentEffect / 100);
      }

      // Use whichever is higher (big hit build OR ailment build)
      ailmentDamage = Math.max(1, Math.floor(Math.max(hitBasedDamage, effectBasedDamage)));
    }

    return {
      type: ailmentType,
      chance: effectiveChance,
      damage: ailmentDamage,
      applied,
    };
  }

  /**
   * Check if position is in attack range
   */
  static isInRange(
    attackerX: number,
    attackerY: number,
    targetX: number,
    targetY: number,
    range: number
  ): boolean {
    const dx = targetX - attackerX;
    const dy = targetY - attackerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance <= range;
  }

  /**
   * Calculate beam damage per tick (for channeled beam skills)
   * @param baseDamage - Base damage from player stats
   * @param dpsMultiplier - DPS multiplier from skill (0.5-2.0)
   * @param deltaTime - Time since last tick
   */
  static calculateBeamDamage(
    baseDamage: number,
    dpsMultiplier: number,
    deltaTime: number,
    calculator?: StatCalculator
  ): number {
    let damage = baseDamage * dpsMultiplier * deltaTime;

    // Apply damage modifiers if calculator provided
    if (calculator) {
      const damageMod = calculator.calculate('damage', 100) / 100;
      damage *= damageMod;
    }

    return Math.max(1, Math.floor(damage));
  }

  /**
   * Calculate trap explosion damage
   * @param baseDamage - Trap's base damage
   * @param distanceFromCenter - Distance from trap center
   * @param explosionRadius - Trap's explosion radius
   */
  static calculateTrapDamage(
    baseDamage: number,
    distanceFromCenter: number,
    explosionRadius: number
  ): number {
    // Damage falloff: full damage at center, 50% at edge
    const falloffPercent = 0.5;
    const normalizedDist = Math.min(1, distanceFromCenter / explosionRadius);
    const falloffMult = 1 - normalizedDist * (1 - falloffPercent);

    return Math.max(1, Math.floor(baseDamage * falloffMult));
  }

  /**
   * Calculate totem attack damage
   * @param baseDamage - Totem's base damage
   * @param attackerCalculator - Optional calculator for damage mods
   */
  static calculateTotemDamage(
    baseDamage: number,
    attackerCalculator?: StatCalculator
  ): number {
    let damage = baseDamage;

    // Apply damage modifiers if calculator provided
    if (attackerCalculator) {
      const damageMod = attackerCalculator.calculate('damage', 100) / 100;
      damage *= damageMod;
    }

    return Math.max(1, Math.floor(damage));
  }

  /**
   * Calculate aura effect value
   * @param baseValue - Base effect value
   * @param effectStrength - Aura's effect strength multiplier (0.5-2.0)
   */
  static calculateAuraEffect(
    baseValue: number,
    effectStrength: number
  ): number {
    return baseValue * effectStrength;
  }

  /**
   * Get all entities in a circular area
   * Now accounts for entity hitbox radius for more accurate collision
   */
  static getEntitiesInRadius<T extends { position: { x: number; y: number }; width?: number; height?: number }>(
    entities: T[],
    centerX: number,
    centerY: number,
    radius: number
  ): T[] {
    return entities.filter((entity) => {
      const dx = entity.position.x - centerX;
      const dy = entity.position.y - centerY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Calculate entity's hitbox radius if width/height available
      let entityRadius = 0;
      if (entity.width !== undefined && entity.height !== undefined) {
        entityRadius = (entity.width + entity.height) / 4;
      }

      // Entity is hit if distance minus entity radius is within attack radius
      return distance <= radius + entityRadius;
    });
  }

  /**
   * Get all entities in a cone area
   * Now accounts for entity hitbox radius
   */
  static getEntitiesInCone<T extends { position: { x: number; y: number }; width?: number; height?: number }>(
    entities: T[],
    originX: number,
    originY: number,
    directionX: number,
    directionY: number,
    range: number,
    angleRadians: number
  ): T[] {
    return entities.filter((entity) => {
      const dx = entity.position.x - originX;
      const dy = entity.position.y - originY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Calculate entity's hitbox radius
      let entityRadius = 0;
      if (entity.width !== undefined && entity.height !== undefined) {
        entityRadius = (entity.width + entity.height) / 4;
      }

      if (distance > range + entityRadius || distance === 0) return false;

      // Normalize vectors
      const ndx = dx / distance;
      const ndy = dy / distance;
      const dirLength = Math.sqrt(directionX * directionX + directionY * directionY);
      const normDirX = directionX / dirLength;
      const normDirY = directionY / dirLength;

      // Calculate angle between direction and entity
      const dot = ndx * normDirX + ndy * normDirY;
      const angle = Math.acos(Math.min(1, Math.max(-1, dot)));

      // Add some tolerance based on entity size (larger entities easier to hit)
      const angleTolerance = entityRadius > 0 ? Math.atan2(entityRadius, distance) : 0;

      return angle <= (angleRadians / 2) + angleTolerance;
    });
  }
}
